// New Distribution for a creative (7.6F.2): where the Tag will be used
// (searchable platform catalog, or the workspace's own platform), the format
// and honest compatibility. The API creates the permanent Tag in the same
// transaction. A creative may have several Distributions (one per platform /
// format) without being duplicated. When the creative already has a
// published version, the new Distribution can start serving it right away.
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { api, call } from "@/api/client";
import { keys } from "@/api/hooks";
import type { CreativeProduct } from "@/api/types";
import { Button } from "@/ui/button";
import { Dialog, DialogContent, DialogFooter } from "@/ui/dialog";
import { notifyError } from "../common/notify";
import { DistributionForm, useVersionFormats } from "./distribution-form";

export function DistributionDialog({
  orgId,
  creativeId,
  product,
  formatsFromVersionId,
  publishedVersion,
  canAddPlatform,
  open,
  onOpenChange,
}: {
  orgId: string;
  creativeId: string;
  product: CreativeProduct;
  /** Version whose document formats are offered (the latest one). */
  formatsFromVersionId: string | undefined;
  /** Latest PUBLISHED version, if any: offered for immediate activation. */
  publishedVersion: { id: string; versionNumber: number } | undefined;
  canAddPlatform: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const formats = useVersionFormats(orgId, open ? formatsFromVersionId : undefined);
  const [activate, setActivate] = useState(true);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Nueva distribución"
        description="Elige dónde vas a utilizar el Tag. La plataforma y el formato no cambian después: para otra plataforma, crea otra distribución."
        className="max-h-[85vh] max-w-xl overflow-y-auto"
      >
        {open && (formats.isSuccess || !formatsFromVersionId || formats.isError) ? (
          <DistributionForm
            key={formats.data?.join(",") ?? "none"}
            orgId={orgId}
            creativeId={creativeId}
            product={product}
            formats={formats.data ?? []}
            canAddPlatform={canAddPlatform}
            submitLabel="Crear distribución y Tag"
            pendingLabel="Creando…"
            after={
              publishedVersion ? (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={activate}
                    onChange={(e) => {
                      setActivate(e.target.checked);
                    }}
                  />
                  Activar la versión publicada {publishedVersion.versionNumber} en esta distribución
                </label>
              ) : null
            }
            onCreated={async (created) => {
              if (publishedVersion && activate) {
                await call(() =>
                  api.PUT("/orgs/{orgId}/distributions/{distributionId}/active-version", {
                    params: { path: { orgId, distributionId: created.id } },
                    body: { versionId: publishedVersion.id },
                  })
                ).catch(notifyError);
              }
              await queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
              onOpenChange(false);
              await navigate({ to: `/o/${orgId}/distributions/${created.id}`, search: { created: 1 } });
            }}
            footer={(submit) => (
              <DialogFooter>
                <Button
                  variant="secondary"
                  onClick={() => {
                    onOpenChange(false);
                  }}
                >
                  Cancelar
                </Button>
                {submit}
              </DialogFooter>
            )}
          />
        ) : (
          <p className="text-sm text-muted">Cargando…</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
